import { useTranslation } from 'react-i18next';
import { KoobInputDate, KoobInputStepper } from '@koob/ui';
import { formatDate, parseDateOnly, toDateString } from '@koob/dates';

export const newRoom = () => ({
  adults: 0,
  children: 0,
  childrenBirthdates: [],
});

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export default function RoomsCompositionPicker({
  value,
  onChange,
  maxAdults,
  maxChildren,
  parentChildrenBirthdates = [],
  isVisibleRoom = false,
}) {
  const { t } = useTranslation('hotel');

  const updateRoom = (roomIndex, updates) => {
    onChange(value.map((r, i) => (i === roomIndex ? { ...r, ...updates } : r)));
  };

  const setChildBirthdate = (roomIndex, childIndex, date) => {
    onChange(
      value.map((r, i) => {
        if (i !== roomIndex) return r;
        const birthdates = [...(r.childrenBirthdates || [])];
        birthdates[childIndex] = date;
        return { ...r, childrenBirthdates: birthdates };
      }),
    );
  };

  const totalAllocatedAdults = value.reduce(
    (sum, r) => sum + (r.adults ?? 0),
    0,
  );
  const totalAllocatedChildren = value.reduce(
    (sum, r) => sum + (r.children ?? 0),
    0,
  );

  // Build parent pool as normalized date keys ('YYYY-MM-DD')
  const parentPool = parentChildrenBirthdates.filter(Boolean).map(toDateString);
  const hasParentPool = parentPool.length > 0;

  const parentCount = parentPool.reduce((acc, key) => {
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="border border-dashed border-gray-300 rounded-xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('getAvailabilityModal.roomComposition')}
        </span>
        {maxAdults !== undefined && (
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-500">
              {t('getAvailabilityModal.adults')} :{' '}
              <span
                className={`font-semibold ${
                  totalAllocatedAdults !== maxAdults
                    ? 'text-red-500'
                    : 'text-gray-700'
                }`}
              >
                {totalAllocatedAdults}/{maxAdults}
              </span>
            </span>
            {maxChildren !== undefined && (
              <span className="text-gray-500">
                {t('getAvailabilityModal.children')} :{' '}
                <span
                  className={`font-semibold ${
                    totalAllocatedChildren !== maxChildren
                      ? 'text-red-500'
                      : 'text-gray-700'
                  }`}
                >
                  {totalAllocatedChildren}/{maxChildren}
                </span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Rooms */}
      {value.map((room, roomIndex) => {
        const otherAdults = value.reduce(
          (sum, r, i) => (i === roomIndex ? sum : sum + (r.adults ?? 0)),
          0,
        );
        const otherChildren = value.reduce(
          (sum, r, i) => (i === roomIndex ? sum : sum + (r.children ?? 0)),
          0,
        );
        const adultsMax =
          maxAdults !== undefined
            ? Math.max(0, maxAdults - otherAdults)
            : undefined;
        const childrenMax =
          maxChildren !== undefined
            ? Math.max(0, maxChildren - otherChildren)
            : undefined;

        // Count only active dates (sliced to children count) across ALL rooms
        const allRoomsSelectedCount = value
          .flatMap(r => (r.childrenBirthdates || []).slice(0, r.children))
          .filter(Boolean)
          .reduce((acc, d) => {
            const key = toDateString(d);
            if (key) acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});

        return (
          <div
            key={roomIndex}
            className="border border-gray-200 rounded-xl px-4 py-3 space-y-3 bg-white"
          >
            {/* Room header */}
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-700 text-sm">
                {t('getAvailabilityModal.room')} {roomIndex + 1}
              </span>
              {value.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    onChange(value.filter((_, i) => i !== roomIndex))
                  }
                  className="text-gray-400 hover:text-red-500 bg-transparent border-none p-1 rounded transition-colors"
                >
                  <TrashIcon />
                </button>
              )}
            </div>

            {/* Adults / Children */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-xs text-gray-500">
                  {t('getAvailabilityModal.adults')}
                </div>
                <KoobInputStepper
                  value={room.adults}
                  min={0}
                  max={adultsMax}
                  onValueChange={adults => updateRoom(roomIndex, { adults })}
                />
              </div>
              <div className="space-y-2">
                <div className="text-xs text-gray-500">
                  {t('getAvailabilityModal.children')}
                </div>
                <KoobInputStepper
                  value={room.children}
                  max={childrenMax}
                  onValueChange={children =>
                    updateRoom(roomIndex, { children })
                  }
                />
              </div>
            </div>

            {/* Children birthdates */}
            {room.children > 0 && (
              <div className="border border-dashed border-gray-200 rounded-xl px-4 py-3 space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('getAvailabilityModal.childBirthdates')}{' '}
                  <span className="text-red-500">*</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: room.children }).map(
                    (_, childIndex) => {
                      const currentKey = toDateString(
                        room.childrenBirthdates?.[childIndex],
                      );

                      if (hasParentPool) {
                        const options = Object.keys(parentCount)
                          .filter(key => {
                            const allowed = parentCount[key];
                            const usedGlobally =
                              allRoomsSelectedCount[key] || 0;
                            const usedByThisSlot = currentKey === key ? 1 : 0;
                            return (
                              key === currentKey ||
                              usedGlobally - usedByThisSlot < allowed
                            );
                          })
                          .map(key => ({
                            label: formatDate(key, 'DD/MM/YYYY'),
                            value: key,
                          }));

                        return (
                          <div
                            key={childIndex}
                            className="space-y-1"
                            style={{
                              zIndex: room.children - childIndex,
                              position: 'relative',
                            }}
                          >
                            <div className="text-sm font-medium text-gray-700">
                              {t('getAvailabilityModal.child')} {childIndex + 1}
                            </div>
                            <select
                              value={currentKey ?? ''}
                              onChange={e =>
                                setChildBirthdate(
                                  roomIndex,
                                  childIndex,
                                  e.target.value || null,
                                )
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-orange-400"
                            >
                              <option value="">—</option>
                              {options.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={childIndex}
                          className="space-y-1"
                          style={{
                            zIndex: room.children - childIndex,
                            position: 'relative',
                          }}
                        >
                          <div className="text-sm font-medium text-gray-700">
                            {t('getAvailabilityModal.child')} {childIndex + 1}
                          </div>
                          <KoobInputDate
                            block
                            value={parseDateOnly(
                              room.childrenBirthdates?.[childIndex],
                            )}
                            onChange={date =>
                              setChildBirthdate(roomIndex, childIndex, date)
                            }
                            maxDate={new Date()}
                          />
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add room */}
      <button
        type="button"
        onClick={() => onChange([...value, newRoom()])}
        className={`w-full flex items-center justify-center gap-2 border rounded-xl px-4 py-2 text-sm font-medium transition-colors outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
          isVisibleRoom
            ? 'bg-orange-500 hover:bg-orange-600 text-white border-orange-500 disabled:hover:bg-orange-500'
            : 'bg-white hover:bg-orange-500 text-gray-600 hover:text-white border-gray-200 disabled:hover:bg-white disabled:hover:text-gray-600'
        }`}
      >
        <span className="text-base leading-none">+</span>
        {t('getAvailabilityModal.addRoom')}
      </button>
    </div>
  );
}
